import { useRef, useState } from 'react'

import { Box, Button, Icon, InfiniteScroll, Text } from '@factorialco/deprecated-design-system'
import { F1SearchBox } from '@factorialco/f0-react/dist/experimental'
import { AtsQuestionTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  AtsAddQuestionFromTemplatesDocument,
  AtsGetJobPostingSettingsApplicationFormDocument,
  AtsGetQuestionTemplatesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import QuestionTemplateCard from 'modules/ats/components/QuestionTemplates/QuestionTemplateCard'
import QuestionTemplateCardSkeleton from 'modules/ats/components/QuestionTemplates/QuestionTemplateCard/QuestionTemplateCardSkeleton'
import Simplechip from 'modules/ats/components/SimpleChip'
import { getQuestionTypeLabel } from 'modules/ats/lib/utils/customQuestion'

interface Props {
  jobId: number
  onClose: () => void
}

const QUESTIONS_TEMPLATE_PER_PAGE = 10
export const AddFromTemplateQuestions = ({ onClose, jobId }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([])

  const filter = selectedFilter !== null ? { questionType: { eq: selectedFilter } } : {}

  const {
    data,
    controls: { fetchMore, hasMore, loading },
  } = usePaginatedQuery(AtsGetQuestionTemplatesDocument, {
    extractConnection: (dataToExtract) => dataToExtract.ats.questionTemplatesConnection,
    variables: {
      first: QUESTIONS_TEMPLATE_PER_PAGE,
      search: search,
      filter: filter,
    },
  })

  const questionTemplates =
    data?.ats.questionTemplatesConnection.edges.map((edge) => edge.node) || []

  const isSelected = (questionId: number) => selectedQuestions.includes(questionId)

  const handleSelectQuestion = (questionId: number) => {
    if (isSelected(questionId))
      setSelectedQuestions((prev) => prev.filter((id) => id !== questionId))
    else setSelectedQuestions((prev) => [...prev, questionId])
  }

  const [addTemplateQuestions] = useMutation(AtsAddQuestionFromTemplatesDocument, {
    extractorFn: (dataToExtract) => dataToExtract.atsMutations.createFromTemplateAtsQuestion,
    refetchQueries: [AtsGetJobPostingSettingsApplicationFormDocument],
  })

  const handleSubmit = async () => {
    const result = await addTemplateQuestions({
      variables: {
        companyId: company.id,
        jobId: jobId,
        questionTemplateIds: selectedQuestions,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }

    return handled
  }

  const saveButtonLabel = (() => {
    const count = selectedQuestions.length

    if (count === 0) return i18n.t('common.add')
    if (count === 1)
      return i18n.t('ats.job_posting_settings.application_form.template_questions.save_button_one')

    return i18n.t(
      'ats.job_posting_settings.application_form.template_questions.save_button_other',
      {
        amount: count,
      }
    )
  })()

  return (
    <Modal
      illustration="template"
      title={i18n.t('ats.job_posting_settings.application_form.template_questions.add_modal_title')}
      onClose={onClose}
      footer={
        <Box gap="s8" flexDirection="row">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={selectedQuestions.length === 0} onClick={() => handleSubmit()}>
            {saveButtonLabel}
          </Button>
        </Box>
      }
    >
      <Box gap="s24" minWidth="s600">
        <Box gap="s6" flexDirection="row">
          <Simplechip
            label={i18n.t('common.all')}
            active={selectedFilter === null}
            onClick={() => setSelectedFilter(null)}
          />

          {AtsQuestionTypeEnumValues.map((type) => (
            <Simplechip
              key={type}
              label={getQuestionTypeLabel(type)}
              onClick={() => setSelectedFilter(type)}
              active={selectedFilter === type}
            />
          ))}
        </Box>
        <F1SearchBox
          placeholder={i18n.t(
            'ats.job_posting_settings.application_form.template_questions.search_placeholder'
          )}
          value={search}
          onChange={setSearch}
          clearable
          autoFocus
        />

        <Box
          ref={scrollContainerRef}
          data-scroll-container
          maxHeight={'s400'}
          overflowY="auto"
          overflowX="hidden"
        >
          <InfiniteScroll
            loadMore={fetchMore}
            hasMore={hasMore}
            initialLoad={false}
            scrollContainerRef={scrollContainerRef}
          >
            {loading ? (
              <QuestionTemplateCardSkeleton />
            ) : (
              <>
                {questionTemplates?.length === 0 && (
                  <Box
                    width="full"
                    height="s300"
                    justifyContent="center"
                    alignItems="center"
                    gap="s12"
                  >
                    <Icon.Large icon="complaints" />
                    <Text.Normal weight="semibold" color="black" size="400">
                      {i18n.t(
                        'ats.job_posting_settings.application_form.template_questions.no_templates_created'
                      )}
                    </Text.Normal>
                    <Text.Normal color="grey800" size="300" weight="normal">
                      {i18n.t(
                        'ats.job_posting_settings.application_form.template_questions.no_templates_created_description'
                      )}
                    </Text.Normal>
                    <Box paddingTop="s12">
                      <Button.Router
                        iconName="add"
                        to={navigation.settings.ats.templates.fullPath()}
                      >
                        {i18n.t(
                          'ats.job_posting_settings.application_form.template_questions.create_new_template'
                        )}
                      </Button.Router>
                    </Box>
                  </Box>
                )}
                <Box gap="s12">
                  {questionTemplates.map((item) => (
                    <QuestionTemplateCard
                      key={item.id}
                      label={item.label}
                      mandatory={item.mandatory}
                      autoDisqualify={item.autoDisqualify}
                      questionType={item.questionType}
                      onChange={() => handleSelectQuestion(item.id)}
                      isSelected={isSelected(item.id)}
                      id={item.id}
                    />
                  ))}
                </Box>
              </>
            )}
          </InfiniteScroll>
        </Box>
      </Box>
    </Modal>
  )
}
